import { CustomerRule, RuleFor } from '../../../../dto/admin-rule-models';
import { strings } from '../../../../lang';
import { useState, type SyntheticEvent } from 'react';
import { App, Button, Card, Dropdown, Tag, Tooltip, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { CustomerRuleForm } from './customer-rule-form';
import { CopyOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined, MoreOutlined } from '@ant-design/icons';

const getRuleForLabel = (rf: RuleFor): string => {
  const cjp = strings.customerJourneyPlanner;
  switch (rf) {
    case RuleFor.OpportunitySync: return cjp.outcomeOpportunitySync;
    case RuleFor.CalendarDisplay: return cjp.outcomeCalendarDisplay;
    case RuleFor.SlotBooking: return cjp.outcomeSlotBooking;
    case RuleFor.AgentsView: return cjp.outcomeAgentsView;
    default: return rf;
  }
};

const { Title } = Typography;

const formatDate = (date: Date | string | undefined) => {
  if (!date) return null;
  return new Date(date).toLocaleDateString(undefined, { dateStyle: 'medium' });
};

const splitOrderPrefix = (title: string) => {
  const match = title.match(/^(\d+[a-zA-Z]?)\s+(.+)$/);
  return match ? { prefix: match[1], title: match[2] } : { title };
};

type CustomerRuleCardProps = {
  rule: CustomerRule;
  onSubmit: (rule: CustomerRule) => void;
  onDuplicate: (duplicatedRule: CustomerRule) => void;
  shouldDisplayAddButton: (status: boolean) => void;
};

export const CustomerRuleCard = ({ rule, onSubmit, onDuplicate, shouldDisplayAddButton }: CustomerRuleCardProps) => {
  const { modal } = App.useApp();
  const [shouldEdit, setShouldEdit] = useState(false);
  const onSubmitEdit = (rule: CustomerRule) => {
    onSubmit(rule);
    setShouldEdit(false);
  };

  const setEditDisplay = (status: boolean) => {
    shouldDisplayAddButton(!status);
    setShouldEdit(status);
  };

  const handleDuplicate = () => {
    const duplicated = { ...rule };
    duplicated.id = 0;
    duplicated.feId = crypto.randomUUID();
    duplicated.title = `${duplicated.title} ${strings.customerJourneyPlanner.duplicateSuffix}`;
    onDuplicate(duplicated);
  };

  const deleteRule = () => {
    const updatedRule = { ...rule, isDeleted: true };
    onSubmit(updatedRule);
  };

  const showDeleteConfirm = () => {
    modal.confirm({
      title: strings.customerJourneyPlanner.deleteConfirmTitle,
      icon: <DeleteOutlined />,
      content: strings.customerJourneyPlanner.deleteConfirmContent,
      okText: strings.customerJourneyPlanner.deleteConfirmOk,
      okType: 'danger',
      cancelText: strings.customerJourneyPlanner.deleteConfirmCancel,
      onOk() {
        deleteRule();
      },
    });
  };

  const openEditor = () => setEditDisplay(true);
  const stopCardClick = (event: SyntheticEvent) => event.stopPropagation();
  const displayedTitle = splitOrderPrefix(rule.title);
  const overflowItems: MenuProps['items'] = [
    {
      key: 'duplicate',
      label: strings.customerJourneyPlanner.duplicate,
      icon: <CopyOutlined />,
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        handleDuplicate();
      },
    },
    { type: 'divider' },
    {
      key: 'delete',
      label: strings.customerJourneyPlanner.delete,
      icon: <DeleteOutlined />,
      danger: true,
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        showDeleteConfirm();
      },
    },
  ];

  return (
    <Card className="customer-rule__card" style={{ marginBottom: '12px' }} size={shouldEdit ? 'default' : 'small'} onClick={shouldEdit ? undefined : openEditor}>
      {shouldEdit ? (
        <CustomerRuleForm rule={rule} onSubmit={onSubmitEdit} onCancel={() => setEditDisplay(false)} />
      ) : (
        <>
          <div className="customer-rule__card-header">
            <div className="customer-rule__title-group">
              {displayedTitle.prefix && <span className="customer-rule__order-badge" aria-hidden="true">{displayedTitle.prefix}</span>}
              <Tooltip title={rule.title}>
                <Title className="customer-rule__title" level={4} aria-label={rule.title}>{displayedTitle.title}</Title>
              </Tooltip>
              {rule.description && (
                <Tooltip title={rule.description} placement="right">
                  <InfoCircleOutlined style={{ color: 'var(--text-secondary)', cursor: 'help', fontSize: 14, flexShrink: 0 }} />
                </Tooltip>
              )}
            </div>
            <div className="customer-rule__actions" onClick={stopCardClick}>
              <Button onClick={openEditor} icon={<EditOutlined />}>
                {strings.customerJourneyPlanner.edit}
              </Button>
              <Dropdown menu={{ items: overflowItems }} trigger={['click']}>
                <Button aria-label={strings.customerJourneyPlanner.ruleActions} icon={<MoreOutlined />} />
              </Dropdown>
            </div>
          </div>
          <div className="customer-rule__metadata">
            {[...new Set(rule.outcomes.map((o) => o.ruleFor))].map((rf) => (
              <Tag key={rf} className={`customer-rule__outcome-tag customer-rule__outcome-tag--${rf}`}>
                {getRuleForLabel(rf)}
              </Tag>
            ))}
            {rule.createdBy && (
              <span>{strings.customerJourneyPlanner.by} {rule.createdBy}</span>
            )}
            {rule.lastModifiedDate && (
              <span>{strings.customerJourneyPlanner.modified} {formatDate(rule.lastModifiedDate)}</span>
            )}
          </div>
        </>
      )}
    </Card>
  );
};
